import { useState } from "react";
import {
  Button,
  Checkbox,
  Container,
  Group,
  Paper,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { TextInput } from "react-hook-form-mantine";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  lookupWorkspaceAccount,
  useInviteToWorkspace,
} from "../../../api/workspace";
import AddExistingAccountModal from "./AddExistingAccountModal";
import { WorkspaceMembersTable } from "./WorkspaceMembersTable";
import { PendingInvitationsTable } from "./PendingInvitationsTable";
import { web_inviteToWorkspaceSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";

export default function AddUserToWorkspace({
  canManageMembers,
  isSysAdmin,
  workspace,
}) {
  const { id } = useParams();
  const [isLookingUp, setIsLookingUp] = useState(false);
  // The matched account waiting for the owner's go-ahead. Kept after the
  // modal closes so its content stays put during the close transition.
  const [match, setMatch] = useState(null);
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  // sysAdmin-only: add or record without emailing anyone. Deliberately not
  // part of the form reset — setup ahead of time is usually several invites
  // in a row with the same choice.
  const [withoutEmail, setWithoutEmail] = useState(false);
  const sendEmail = !(isSysAdmin && withoutEmail);

  const { mutate: invite, isPending: isInviting } = useInviteToWorkspace({
    workspaceId: id,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: { email: "" },
    resolver: resolver(web_inviteToWorkspaceSchema),
  });

  const sendInvite = (email, onSuccess) =>
    invite(
      { email, sendEmail },
      {
        onSuccess: () => {
          reset({ email: "" });
          onSuccess?.();
        },
      }
    );

  // An existing account is added the moment it is invited, so the owner sees
  // who that is and confirms first. An address with no account goes straight
  // to an emailed invitation, and one that is already a member goes straight
  // to the API's "already a member" rejection.
  const onSubmit = async ({ email }) => {
    setIsLookingUp(true);

    let account;
    try {
      account = await lookupWorkspaceAccount({ workspaceId: id, email });
    } catch (error) {
      notifications.show({
        title: "Error",
        color: "red",
        message: error.message,
      });
      return;
    } finally {
      setIsLookingUp(false);
    }

    if (!account.accountExists || account.alreadyMember) {
      sendInvite(email);
      return;
    }

    setMatch({ email, account });
    openModal();
  };

  const isBusy = isInviting || isLookingUp;

  return (
    <Container size={700} mt={16}>
      <Title order={2}>Users in workspace</Title>
      <Paper withBorder shadow="md" p={16} radius="md">
        {canManageMembers && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Group align="flex-end" gap="sm">
              <TextInput
                label="Invite a user by email"
                placeholder="name@company.com"
                name="email"
                control={control}
                style={{ flex: 1 }}
                disabled={isBusy}
              />
              <Button
                type="submit"
                loading={isBusy}
                disabled={!isDirty || isBusy}
              >
                Invite
              </Button>
            </Group>
            <Text size="xs" c="dimmed" mt={6}>
              Someone with a Congrs account is added right away; anyone else
              gets an email invitation.
            </Text>
            {isSysAdmin && (
              <Paper withBorder radius="md" p="sm" mt="sm" bg="gray.0">
                <Checkbox
                  size="sm"
                  label="Don't send an email"
                  description="Adds the account, or records the invitation as not sent. You can send the invitation email later."
                  checked={withoutEmail}
                  onChange={(event) =>
                    setWithoutEmail(event.currentTarget.checked)
                  }
                  disabled={isBusy}
                />
              </Paper>
            )}
          </form>
        )}

        {canManageMembers && (
          <PendingInvitationsTable canManageMembers={canManageMembers} />
        )}

        <WorkspaceMembersTable
          workspace={workspace}
          canManageMembers={canManageMembers}
          isSysAdmin={isSysAdmin}
        />
      </Paper>

      <AddExistingAccountModal
        opened={modalOpened}
        account={match?.account}
        email={match?.email}
        workspaceName={workspace?.name}
        sendEmail={sendEmail}
        isAdding={isInviting}
        onConfirm={() => sendInvite(match.email, closeModal)}
        onClose={closeModal}
      />
    </Container>
  );
}
